import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import AspApi from 'api/ApiConfig';
import { cmsApi } from '../api/cmsService';

const initialState = {
  toggleToast: [],
  currentBrand: null,
  currentAccount: null,
  globalLoader: false,
  featureFlags: {
    ams_feature: true,
    api_trigger_feature: true,
    webchat_feature: false,
    keyword_trigger_feature: true,
    rag_whatsapp_chat_system: false,
    whatsapp_flow_feature: false,
    whatsapp_payment_feature: false,
    standalone_broadcasts_sequences_feature: true,
  },

  isSocketConnected: false,

  // sidebar
  expand: true,

  // on-boarding
  acc_id: null,
  brand_id: null,

  // current user
  currentUser: null,
  currentUserLoading: false,
  currentUserError: null,

  brandUserRoles: [],
  isAccountOwner: false,

  accountUpdate: false,

  // CMS Feature Gating Configuration
  brandPlanConfig: {
    brand: null,
    billingPlan: null,
    features: [],
    functions: [],
    featuresMap: {}, // slug -> feature object (O(1) lookup)
    functionsMap: {}, // slug -> function object (O(1) lookup)
    loading: false,
    error: null,
    lastFetched: null, // Unix timestamp
    cacheExpiry: null, // Unix timestamp (lastFetched + 24 hours)
    source: null, // 'persisted' | 'api' | 'stale-cache' | 'default'
  },

  // Default features to enable on error/fallback
  defaultEnabledFeatures: [
    'gwx-ai-biz-suite-msg-solns-wapp-inbox',
    'gwx-ai-biz-suite-crm-solns-crm-contact-mgmt',
    'gwx-ai-biz-suite-msg-solns-phone-numbers-wapp',
  ],
};

const fetchCurrentUser = createAsyncThunk(
  'app/fetchCurrentUser',
  async ({ rejectWithValue }) => {
    try {
      const res = await AspApi.get(
        'api/v1/svc/aaa/authentication/current_user',
      );

      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to Fetch Current User');
    }
  },
);

/**
 * Fetch brand plan configuration from CMS service
 * Uses cached data if valid (< 24 hours old), otherwise fetches from API
 * Falls back to stale cache or defaults on error
 */
const fetchBrandPlanConfig = createAsyncThunk(
  'app/fetchBrandPlanConfig',
  async ({ brandId, forceRefresh = false }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { brandPlanConfig } = state.app;

      // Step 1: Check if we already have valid cached data in Redux
      if (!forceRefresh && brandPlanConfig.cacheExpiry) {
        const now = Date.now();

        // Use cached data if:
        // 1. Cache is not expired (< 24 hours)
        // 2. Brand ID matches current request
        if (
          now < brandPlanConfig.cacheExpiry &&
          brandPlanConfig.brand?.id === brandId
        ) {
          return {
            data: {
              brand: brandPlanConfig.brand,
              billing_plan: brandPlanConfig.billingPlan,
              features: brandPlanConfig.features,
              functions: brandPlanConfig.functions,
            },
            source: 'persisted',
          };
        }
      }

      // Step 2: Cache expired or brand changed, fetch from API
      const response = await cmsApi.getBrandPlanConfiguration(brandId);

      return {
        data: response.data,
        source: 'api',
      };
    } catch (error) {
      console.error('[Feature Gating] API failed:', error);

      const state = getState();
      const { brandPlanConfig } = state.app;

      // Step 3: On error, use stale Redux cache if available
      if (brandPlanConfig.features.length > 0) {
        console.warn(
          '[Feature Gating] Using stale Redux cache as fallback',
        );
        return {
          data: {
            brand: brandPlanConfig.brand,
            billing_plan: brandPlanConfig.billingPlan,
            features: brandPlanConfig.features,
            functions: brandPlanConfig.functions,
          },
          source: 'stale-cache',
          error: error.message,
        };
      }

      // Step 4: No cache available, will use defaults in reducer
      return rejectWithValue(error.message || 'Failed to fetch brand plan configuration');
    }
  },
);

export const authSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateToggleToast: (state, action) => {
      state.toggleToast = action.payload;
    },
    updateCurrentUser: (state, action) => {
      const account_details = {
        ...action.payload,
        account_created:
          action?.payload?.active_accounts?.length > 0 ? true : false,
        brand_created: false,
      };
      state.currentUser = action.payload;
    },
    updateAccountDetails: (state, action) => {
      state.currentAccount = action?.payload?.currentAccount;
      state.currentBrand = action?.payload?.currentBrand;

      // Extract feature flags from currentBrand
      if (action?.payload?.currentBrand?.feature_flag) {
        state.featureFlags = action?.payload?.currentBrand?.feature_flag;
      }
    },
    updateCurrentBrand: (state, action) => {
      state.currentBrand = action.payload;

      // Extract feature flags from brand
      if (action?.payload?.feature_flag) {
        state.featureFlags = action?.payload?.feature_flag;
      }
    },
    reset_feature_flag: (state, action) => {
      state.featureFlags = {
        ams_feature: true,
        api_trigger_feature: true,
        webchat_feature: false,
        keyword_trigger_feature: true,
        rag_whatsapp_chat_system: false,
        whatsapp_payment_feature: false,
        whatsapp_flow_feature: false,
        standalone_broadcasts_sequences_feature: true,
      };
    },
    updateCurrentAccount: (state, action) => {
      state.currentAccount = action.payload;
    },
    updateGlobalLoader: (state, action) => {
      state.globalLoader = action.payload;
    },
    setBrandUserRoles: (state, action) => {
      state.isAccountOwner = state.currentAccount?.role === 'Account Owner';
      if (
        state?.acc_id &&
        state?.brand_id &&
        action.payload?.active_accounts?.length > 0
      ) {
        const current_account = action.payload?.active_accounts?.find(
          (account) => account?.account_id === state?.acc_id,
        );

        if (!current_account) {
          console.log('cant locate current account');
          return;
        }
        const current_brand = current_account?.brands;

        if (!current_brand?.length > 0) {
          console.log('User has no brands');
          return;
        }

        const brand_roles = current_brand?.filter(
          (brand) => brand?.brand_id === state?.brand_id,
        );

        if (!brand_roles?.length > 0) {
          console.log('User does not have roles');
          return;
        }

        state.brandUserRoles = brand_roles;
      }
    },
    clear: (state, action) => {
      state.toggleToast = action?.payload?.toggleToast;
      state.currentUser = action?.payload?.currentUser;
      state.currentBrand = action?.payload?.currentBrand;
      state.currentAccount = action?.payload?.currentAccount;
      state.globalLoader = action?.payload?.globalLoader;
    },

    UpdateOnboardingAccountId: (state, action) => {
      const { acc_id } = action.payload;
      state.acc_id = acc_id;
    },

    UpdateOnboardingBrandId: (state, action) => {
      const { brand_id } = action.payload;
      state.brand_id = brand_id;
    },

    UpdateAccountAndBrandId: (state, action) => {
      const { acc_id, brand_id } = action.payload;
      state.acc_id = acc_id;
      state.brand_id = brand_id;
    },
    setUpdateAccount: (state, action) => {
      state.accountUpdate = action.payload;
    },
    setExpand: (state, action) => {
      state.expand = action.payload;
    },
    updateCurrentBrandStatus: (state, action) => {
      state.currentBrand = {
        ...state.currentBrand,
        status: action.payload,
        is_whatsapp_connected: false,
      };
    },
    setSocketConnection: (state, action) => {
      state.isSocketConnected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Current User
      .addCase(fetchCurrentUser.pending, (state) => {
        state.currentUserLoading = true;
        state.currentUserError = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUserLoading = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.currentUserLoading = false;
        state.currentUser = null;
        state.currentUserError = action.error;
      })

      // Fetch Brand Plan Configuration
      .addCase(fetchBrandPlanConfig.pending, (state) => {
        state.brandPlanConfig.loading = true;
        state.brandPlanConfig.error = null;
      })
      .addCase(fetchBrandPlanConfig.fulfilled, (state, action) => {
        const { data, source } = action.payload;

        // Build Maps for O(1) lookup
        const featuresMap = {};
        const functionsMap = {};

        if (data.features) {
          data.features.forEach((feature) => {
            featuresMap[feature.slug] = feature;
          });
        }

        if (data.functions) {
          data.functions.forEach((func) => {
            functionsMap[func.slug] = func;
          });
        }

        const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        const now = Date.now();

        // Update brandPlanConfig with new data and saving in redux
        state.brandPlanConfig = {
          brand: data.brand,
          billingPlan: data.billing_plan,
          features: data.features || [],
          functions: data.functions || [],
          featuresMap,
          functionsMap,
          loading: false,
          error: null,
          lastFetched: now,
          cacheExpiry: now + CACHE_TTL,
          source,
        };
      })
      .addCase(fetchBrandPlanConfig.rejected, (state, action) => {
        state.brandPlanConfig.loading = false;
        state.brandPlanConfig.error = action.payload;

        // Set default enabled features on error
        state.brandPlanConfig.source = 'default';

        // Build default config with only critical features enabled
        const defaultFeatures = state.defaultEnabledFeatures.map((slug) => ({
          slug,
          is_enabled: true,
          name: slug,
          is_chargeable: false,
          charges: '0.0000',
          limit: null,
        }));

        const featuresMap = {};
        defaultFeatures.forEach((f) => {
          featuresMap[f.slug] = f;
        });

        state.brandPlanConfig.features = defaultFeatures;
        state.brandPlanConfig.featuresMap = featuresMap;
        state.brandPlanConfig.functionsMap = {};
      });
  },
});

export const {
  updateToggleToast,
  updateCurrentUser,
  updateCurrentBrand,
  updateAccountDetails,
  updateGlobalLoader,
  updateCurrentAccount,
  clear,
  UpdateOnboardingAccountId,
  UpdateOnboardingBrandId,
  UpdateAccountAndBrandId,
  setUpdateAccount,
  setExpand,
  updateCurrentBrandStatus,
  setBrandUserRoles,
  setSocketConnection,
  reset_feature_flag,
} = authSlice.actions;

export default authSlice.reducer;

export { fetchCurrentUser, fetchBrandPlanConfig };
